// react
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import PropTypes from 'prop-types';

import { useSnackbar } from 'notistack';

import { FormikProvider, useFormik } from 'formik';

// IconButton
import { Icon } from '@iconify/react';
import CloseIcon from '@iconify/icons-mdi/close-circle-outline';
import trash2Outline from '@iconify/icons-eva/trash-2-outline';

import saveIcon from '@iconify/icons-eva/save-outline';

// material
import { styled } from '@mui/material/styles';
import { LoadingButton } from '@mui/lab';
import {
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button
} from '@mui/material';

// local
import { deleteItem, editItem } from '../../fetch';
import { geofencesActions } from '../../store';
import { useTranslation } from '../../common/LocalizationProvider';
import { selectGeofencesArray } from '../../createSelectorUtils';

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialogContent-root': {
    padding: theme.spacing(2)
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(1)
  }
}));

function BootstrapDialogTitle(props) {
  const { children, onClose, ...other } = props;

  return (
    <DialogTitle sx={{ m: 0, p: 2 }} {...other}>
      {children}
      {onClose ? (
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500]
          }}
        >
          <Icon icon={CloseIcon} />
        </IconButton>
      ) : null}
    </DialogTitle>
  );
}

BootstrapDialogTitle.propTypes = {
  children: PropTypes.node,
  onClose: PropTypes.func.isRequired
};

GeofencesDialogEdit.propTypes = {
  selectedGeofenceId: PropTypes.number.isRequired,
  isOpen: PropTypes.bool,
  setIsOpen: PropTypes.func
};

export default function GeofencesDialogEdit({ selectedGeofenceId, isOpen, setIsOpen }) {
  const t = useTranslation();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const geofences = useSelector(selectGeofencesArray);

  const formik = useFormik({
    initialValues: geofences.find((geofence) => geofence.id === selectedGeofenceId),
    onSubmit: async (values) => {
      const response = await editItem('geofences', values.id, values);
      if (response.ok) {
        enqueueSnackbar(t('Geofences_Edit_Snackbar_Add'), {
          variant: 'success'
        });
        dispatch(geofencesActions.update([await response.json()]));
      } else {
        const error = await response.text();
        const message = error.slice(0, error.indexOf('-'));
        enqueueSnackbar(message, { variant: 'error' });
      }

      setSubmitting(false);
      setIsOpen(false);
    }
  });
  const { handleSubmit, isSubmitting, getFieldProps, setSubmitting } = formik;

  const handelDeleteGeofences = async () => {
    const response = await deleteItem('geofences', selectedGeofenceId);
    if (response.ok) {
      dispatch(geofencesActions.delete(selectedGeofenceId));
      setIsOpen(false);
      enqueueSnackbar(t('Geofences_Edit_Snackbar_Delete'), {
        variant: 'success'
      });
    } else {
      const error = await response.text();
      const message = error.slice(0, error.indexOf('-'));
      enqueueSnackbar(message, { variant: 'error' });
    }
  };

  return (
    <BootstrapDialog open={isOpen}>
      <FormikProvider value={formik}>
        <BootstrapDialogTitle
          onClose={() =>
            geofences.find((geofence) => geofence.id === selectedGeofenceId).name &&
            setIsOpen(false)
          }
        >
          {t('Geofences_Edit')}
        </BootstrapDialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            label={t('Geofences_Edit_Name')}
            {...getFieldProps('name')}
            value={getFieldProps('name').value || ''}
          />
          <TextField
            fullWidth
            label={t('Geofences_Edit_Description')}
            {...getFieldProps('description')}
            value={getFieldProps('description').value || ''}
            sx={{ mt: 3 }}
          />
          <TextField
            fullWidth
            label={t('Geofences_Edit_AltitudeMin')}
            type="number"
            {...getFieldProps('altitudeMin')}
            value={getFieldProps('altitudeMin').value || ''}
            sx={{ mt: 3 }}
          />
          <TextField
            fullWidth
            label={t('Geofences_Edit_AltitudeMax')}
            type="number"
            {...getFieldProps('altitudeMax')}
            value={getFieldProps('altitudeMax').value || ''}
            sx={{ mt: 3 }}
          />
          <TextField
            fullWidth
            label={t('Geofences_Edit_Color')}
            {...getFieldProps('attributes.color')}
            value={getFieldProps('attributes.color').value || ''}
            sx={{ mt: 3 }}
          />
        </DialogContent>
        <DialogActions>
          <LoadingButton
            fullWidth
            size="large"
            type="submit"
            variant="contained"
            loading={isSubmitting}
            startIcon={<Icon icon={saveIcon} />}
            onClick={handleSubmit}
          >
            {t('btn_Save')}
          </LoadingButton>
          <Button
            fullWidth
            size="large"
            type="submit"
            color="inherit"
            variant="outlined"
            onClick={handelDeleteGeofences}
            startIcon={<Icon icon={trash2Outline} />}
          >
            {t('btn_Delete')}
          </Button>
        </DialogActions>
      </FormikProvider>
    </BootstrapDialog>
  );
}
